import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {
  applyPendingIssueSuggestionsMutation,
  applyPendingIssueSuggestionsMutation$data,
} from './__generated__/applyPendingIssueSuggestionsMutation.graphql'

export type ApplySuggestionRef =
  | {kind: 'TYPE'; issueTypeId: string}
  | {kind: 'LABEL'; labelId: string}
  | {kind: 'FIELD'; issueFieldId: string}
  | {kind: 'CLOSE'}

export function commitApplyPendingIssueSuggestionsMutation({
  environment,
  issueId,
  actorId,
  suggestions,
  onError,
  onCompleted,
}: {
  environment: Environment
  issueId: string
  actorId: string
  suggestions: ApplySuggestionRef[]
  onError?: (error: Error) => void
  onCompleted?: (response: applyPendingIssueSuggestionsMutation$data) => void
}) {
  return commitMutation<applyPendingIssueSuggestionsMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation applyPendingIssueSuggestionsMutation($input: ApplyPendingIssueSuggestionsInput!) {
        applyPendingIssueSuggestions(input: $input) {
          clientMutationId
        }
      }
    `,
    variables: {
      input: {
        issueId,
        actorId,
        suggestions: suggestions.map(s => {
          if (s.kind === 'TYPE') return {kind: 'TYPE', issueTypeId: s.issueTypeId}
          if (s.kind === 'LABEL') return {kind: 'LABEL', labelId: s.labelId}
          if (s.kind === 'FIELD') return {kind: 'FIELD', issueFieldId: s.issueFieldId}
          return {kind: 'CLOSE'}
        }),
      },
    },
    optimisticUpdater: store => applyOptimisticUpdate(store, issueId, actorId, suggestions),
    updater: store => applyOptimisticUpdate(store, issueId, actorId, suggestions),
    onError: error => onError?.(error),
    onCompleted: response => onCompleted?.(response),
  })
}

function applyOptimisticUpdate(
  store: RecordSourceSelectorProxy,
  issueId: string,
  actorId: string,
  suggestions: ApplySuggestionRef[],
) {
  const issue = store.get(issueId)
  if (!issue) return

  const pendingSuggestions = issue.getLinkedRecords('pendingSuggestions') ?? []

  for (const suggestion of suggestions) {
    const suggestionTypename =
      suggestion.kind === 'TYPE'
        ? 'PendingTypeSuggestion'
        : suggestion.kind === 'LABEL'
          ? 'PendingLabelSuggestion'
          : suggestion.kind === 'FIELD'
            ? 'PendingFieldSuggestion'
            : 'PendingCloseSuggestion'

    // Mark the matching pending suggestion as APPLIED so the row hides.
    for (const s of pendingSuggestions) {
      if (!s) continue
      if (s.getValue('__typename') !== suggestionTypename) continue
      const actor = s.getLinkedRecord('actor')
      if (actor?.getValue('id') !== actorId) continue
      let matches = false
      if (suggestion.kind === 'TYPE') {
        const issueType = s.getLinkedRecord('issueType')
        matches = issueType?.getValue('id') === suggestion.issueTypeId
      } else if (suggestion.kind === 'LABEL') {
        const label = s.getLinkedRecord('label')
        matches = label?.getValue('id') === suggestion.labelId
      } else if (suggestion.kind === 'FIELD') {
        const issueField = s.getLinkedRecord('issueField')
        matches = issueField?.getValue('id') === suggestion.issueFieldId
      } else {
        // CLOSE: at most one pending close suggestion per actor, matched by actor alone.
        matches = true
      }
      if (matches) {
        // Relay stores fields under the schema name (`state`), not the fragment alias.
        s.setValue('APPLIED', 'state')
      }
    }
  }
}
